import { useEffect, useState } from "react";

import { DatePicker, fromIsoDate, toIsoDate } from "@/components/cinch/DatePicker";
import { Field } from "@/components/cinch/Field";
import { CheckboxList } from "@/components/cinch/FilterBar";
import { NumberInput } from "@/components/cinch/NumberInput";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import {
  CARRIER_LABEL,
  SHIPMENT_LABEL,
  fmtMoney,
  useCreateShipment,
  type Carrier,
  type Disbursement,
  type ShipmentStatus,
} from "../api";

const CARRIERS: Carrier[] = ["fedex", "usps", "ups", "dhl", "other"];
const STATUSES: ShipmentStatus[] = [
  "pending",
  "label_created",
  "in_transit",
  "delivered",
  "exception",
];

/**
 * "Record a shipment" — the manual half of tracking, and the only way a box
 * gets into the system until carrier credentials exist.
 *
 * A shipment is a BOX: it can carry several giveaways. The caller passes the
 * candidate ledger rows (already grouped by where they're going) and every one
 * is ticked by default, because the common case is "everything for this
 * address went in one package". Untick anything that didn't.
 *
 * Freight entered here is split pro-rata across whatever ends up in the box,
 * so the per-recipient cost stays answerable — the thing the spreadsheet
 * couldn't do.
 */
export function ShipmentDialog({
  open,
  onOpenChange,
  candidates,
  defaultAddress,
  defaultName,
  defaultPhone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidates: Disbursement[];
  defaultAddress?: string | null;
  defaultName?: string | null;
  defaultPhone?: string | null;
}) {
  const create = useCreateShipment();
  const [selected, setSelected] = useState<string[]>([]);
  const [carrier, setCarrier] = useState<string>("");
  const [tracking, setTracking] = useState("");
  const [service, setService] = useState("");
  const [cost, setCost] = useState<number | null>(null);
  const [shippedAt, setShippedAt] = useState("");
  const [status, setStatus] = useState<ShipmentStatus>("in_transit");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");

  // Reset to the group's defaults each time it opens, so reopening on a
  // different address never inherits the last one's fields.
  useEffect(() => {
    if (!open) return;
    setSelected(candidates.map((c) => c.id));
    setCarrier("");
    setTracking("");
    setService("");
    setCost(null);
    // Local date, not the UTC slice — west of UTC that renders yesterday.
    setShippedAt(toIsoDate(new Date()));
    setStatus("in_transit");
    setName(defaultName ?? candidates[0]?.recipient_name ?? "");
    setAddress(defaultAddress ?? "");
    setPhone(defaultPhone ?? "");
    setNotes("");
    create.reset();
    // Only re-seed on open; `candidates` is a fresh array every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const units = candidates
    .filter((c) => selected.includes(c.id))
    .reduce((sum, c) => sum + c.quantity, 0);
  const perUnit = cost != null && units > 0 ? cost / units : null;

  const submit = () => {
    create.mutate(
      {
        disbursement_ids: selected,
        carrier: carrier || null,
        carrier_service: service || null,
        tracking_number: tracking || null,
        ship_to_name: name || null,
        ship_to_address: address || null,
        ship_to_phone: phone || null,
        shipping_cost: cost,
        shipped_at: shippedAt || null,
        status,
        notes: notes || null,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record a shipment</DialogTitle>
          <DialogDescription>
            One box, however many gifts are in it. The shipping cost splits
            pro-rata across whatever you tick, so each recipient carries its
            true share into the accounting.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label>What&apos;s in the box</Label>
            <div className="rounded-xl border p-3">
              <CheckboxList
                options={candidates.map((row) => ({
                  value: row.id,
                  // `label` is what search and screen readers use; `render`
                  // is what the eye needs.
                  label: `${row.item_name} ${row.recipient_name ?? row.occasion ?? ""}`.trim(),
                  render: (
                    <span className="flex flex-wrap items-baseline gap-1.5">
                      <span className="font-medium">
                        {row.item_name}
                        {row.quantity > 1 && ` x${row.quantity}`}
                      </span>
                      <span className="text-muted-foreground">
                        {row.recipient_name ?? row.occasion ?? "—"}
                        {row.recipient_company_name &&
                          ` @ ${row.recipient_company_name}`}
                      </span>
                    </span>
                  ),
                }))}
                selected={selected}
                onChange={setSelected}
                maxHeight={200}
              />
            </div>
            {perUnit != null && selected.length > 1 && (
              <p className="text-xs text-muted-foreground">
                {fmtMoney(cost)} across {units} unit
                {units === 1 ? "" : "s"} = {fmtMoney(perUnit)} each in the
                department roll-up.
              </p>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ship-carrier">Carrier</Label>
              <Select value={carrier} onValueChange={setCarrier}>
                <SelectTrigger id="ship-carrier">
                  <SelectValue placeholder="Detect from tracking number" />
                </SelectTrigger>
                <SelectContent>
                  {CARRIERS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {CARRIER_LABEL[c]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Field
              id="ship-tracking"
              label="Tracking number"
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              helper="Leave the carrier blank and we'll infer it from this"
            />
            <Field
              id="ship-service"
              label="Service (Ground, Home Delivery…)"
              value={service}
              onChange={(e) => setService(e.target.value)}
            />
            <NumberInput
              id="ship-cost"
              label="Shipping cost"
              prefix="$"
              value={cost}
              onChange={setCost}
            />
            <DatePicker
              id="ship-date"
              value={fromIsoDate(shippedAt)}
              onChange={(d) => setShippedAt(d ? toIsoDate(d) : "")}
              aria-label="Shipped on"
            />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ship-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as ShipmentStatus)}
              >
                <SelectTrigger id="ship-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {SHIPMENT_LABEL[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Field
              id="ship-name"
              label="Ship to name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Field
              id="ship-address"
              label="Ship to address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
            <Field
              id="ship-phone"
              label="Phone number"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              helper="Carriers ask for one on delivery"
            />
          </div>

          <Textarea
            placeholder="Notes (packed with a card, sent from the Lehi office…)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />

          {selected.length === 0 && (
            <Alert variant="warning">Tick at least one thing to ship.</Alert>
          )}
          {create.error && <Alert variant="danger">{String(create.error)}</Alert>}
        </div>

        <DialogFooter className="items-center">
          <Badge variant="neutral" className="mr-auto">
            {selected.length} of {candidates.length} selected
          </Badge>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={selected.length === 0 || create.isPending}
            onClick={submit}
          >
            {create.isPending ? "Saving…" : "Record shipment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
